/** Normalizes the North Carolina foreclosure docket formats present in county
 * sale exports. The legacy Toolbench ID remains `sp_code_lookup`, but judicial
 * foreclosure rows can use CV/CVS dockets and reach the same source-backed
 * document/evidence pipeline. Older county exports also use compact SP
 * numbers such as `22 SP 18`, so an SP docket may have fewer than four digits
 * after its prefix. */
const DOCKET_BODY = /^(?:\d{2}SP\d{1,8}(?:-\d{3})?|\d{2}CVS?\d{4,8}(?:-\d{3})?)$/;
const DOCKET_IN_TEXT = /\b(\d{2}[-\s]?(?:SP|CVS?)[-\s]?\d{1,8}(?:-\d{3})?)\b/gi;

export function normalizeNcForeclosureCaseNumber(value: string): string | null {
  const normalized = String(value ?? "")
    .replaceAll(/\s+/g, "")
    .toUpperCase()
    .replace(/^(\d{2})-(SP|CVS?)/, "$1$2");
  return DOCKET_BODY.test(normalized) ? normalized : null;
}

/** Hyphenated and spaced spellings that county trees actually store. */
export function courtDocketSearchVariants(value: string): string[] {
  const normalized = normalizeNcForeclosureCaseNumber(value);
  if (!normalized) return [];
  const parsed = /^(?<year>\d{2})(?<kind>SP|CVS?)(?<digits>\d{1,8})(?<suffix>-\d{3})?$/.exec(normalized);
  if (!parsed?.groups) return [normalized];
  const year = parsed.groups.year ?? "";
  const kind = parsed.groups.kind ?? "";
  const digits = parsed.groups.digits ?? "";
  const suffix = parsed.groups.suffix ?? "";
  return [...new Set([
    normalized,
    `${year}-${kind}${digits}${suffix}`,
    `${year} ${kind}${digits}${suffix}`,
    `${year} ${kind} ${digits}${suffix}`,
    `${year}-${kind}-${digits}${suffix}`
  ].map((entry) => entry.toUpperCase()))];
}

export function extractNormalizedCourtDockets(value: unknown): string[] {
  const blob = docketBlob(value);
  const found: string[] = [];
  const seen = new Set<string>();
  // Clone: the module regex is /g and Workers share isolates across requests.
  const pattern = new RegExp(DOCKET_IN_TEXT.source, DOCKET_IN_TEXT.flags);
  for (const match of blob.matchAll(pattern)) {
    const normalized = normalizeNcForeclosureCaseNumber(match[1] ?? "");
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    found.push(normalized);
  }
  return found;
}

/** D1 json_set columns come back as objects. BigInt/circular values must not 500 the lane list. */
function docketBlob(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value ?? "", (_key, item) => typeof item === "bigint" ? String(item) : item) ?? "";
  } catch {
    return "";
  }
}

export function sourceFieldsIncludeCourtDocket(sourceFields: unknown, requested: string): boolean {
  const want = normalizeNcForeclosureCaseNumber(requested);
  if (!want) return false;
  return extractNormalizedCourtDockets(sourceFields).includes(want);
}
